import React, { useEffect, useState } from "react";
import { Box, Button, Paper, Tooltip, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

interface CardAction {
  id: string;
  label: string;
  params?: any;
  followup_message?: string;
}

interface HealthScoreCardProps {
  card: any;
  onAction?: (action: CardAction) => void;
}

const HealthScoreCard: React.FC<HealthScoreCardProps> = ({ card, onAction }) => {
  const handleItemAction = (action?: CardAction) => {
    if (!action) return;
    onAction?.(action);
  };

  const normalize = (value?: string) => (value || "").toLowerCase().trim();

  const titleScoreMatch = String(card?.title || "").match(/(\d+)/);
  const titleScore = titleScoreMatch ? Number(titleScoreMatch[1]) : 0;
  const scoreHighlightItem = (card?.sections || []).find(
    (s: any) => normalize(s?.heading) === normalize("Score Highlight")
  )?.items?.[0];
  const highlightLabel = normalize(scoreHighlightItem?.label || "");
  const highlightValueMatch = String(scoreHighlightItem?.value ?? "").match(/(\d+)/);
  const highlightScore = highlightValueMatch ? Number(highlightValueMatch[1]) : NaN;
  const isHighlightScore =
    (highlightLabel.includes("current") && highlightLabel.includes("score")) ||
    (highlightLabel.includes("previous") && highlightLabel.includes("score"));
  const score =
    isHighlightScore && Number.isFinite(highlightScore) ? highlightScore : titleScore;

  const [animatedScore, setAnimatedScore] = useState(0);
  const trendText = card?.subtitle?.replace(/^trend:\s*/i, "") || "Stable";
  const trendKey = normalize(trendText);
  const trendColor =
    trendKey.includes("decrease") || trendKey.includes("down")
      ? "#E53935"
      : trendKey.includes("increase") || trendKey.includes("up")
      ? "#2E7D32"
      : "#6B7280";
  const TrendIcon =
    trendKey.includes("decrease") || trendKey.includes("down")
      ? TrendingDownIcon
      : trendKey.includes("increase") || trendKey.includes("up")
      ? TrendingUpIcon
      : TrendingFlatIcon;

  const formatAsOf = (iso?: string) => {
    if (!iso) return "";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getSection = (heading: string) =>
    (card?.sections || []).find((s: any) => normalize(s?.heading) === normalize(heading));

  const scoreTrendItems = getSection("Score Trend")?.items || [];
  const keyImpactItems = getSection("Key Impact Factors")?.items || [];
  const weeklySummaryItems = getSection("Weekly Summary")?.items || [];
  const actionsItems = getSection("What you can do")?.items || [];

  const parseFactor = (item: any, idx: number) => {
    const text = String(item?.value || "");
    const statusMatch = text.match(/status:\s*([^|]+)/i);
    const impactMatch = text.match(/impact:\s*([^|]+)/i);
    const status = statusMatch?.[1]?.trim() || "";
    const impact = impactMatch?.[1]?.trim() || "";
    const impactKey = normalize(impact);
    const scoreByImpact = impactKey.includes("high")
      ? 85
      : impactKey.includes("medium")
      ? 58
      : 45;
    const colorPalette = ["#6F42C1", "#2BB6A8", "#D64F7D", "#E2872D"];
    return {
      label: item?.label || `Factor ${idx + 1}`,
      status,
      impact,
      barValue: scoreByImpact,
      color: colorPalette[idx % colorPalette.length],
    };
  };

  const factorBars = [
    ...keyImpactItems.map((item: any, idx: number) => parseFactor(item, idx)),
    { label: "Nutrition", status: "", impact: "Medium", barValue: 45, color: "#D64F7D" },
    { label: "Stress", status: "", impact: "Medium", barValue: 68, color: "#E2872D" },
  ];

  const actionIcon = (label: string) => {
    const key = normalize(label);
    if (key.includes("doctor")) return MonitorHeartOutlinedIcon;
    if (key.includes("test")) return ScienceOutlinedIcon;
    if (key.includes("wellness")) return SpaOutlinedIcon;
    return FavoriteBorderIcon;
  };

  useEffect(() => {
    const target = Math.max(0, Math.min(score, 100));
    let frameId = 0;
    let startTime = 0;
    const duration = 900;

    const tick = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setAnimatedScore(Math.round(target * progress));
      if (progress < 1) frameId = window.requestAnimationFrame(tick);
    };

    setAnimatedScore(0);
    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [score]);

  const scoreRingStyle = {
    background: `conic-gradient(#6F42C1 ${Math.max(0, Math.min(animatedScore, 100)) * 3.6}deg, #E5E7EB 0deg)`,
    borderRadius: "50%",
    aspectRatio: "1 / 1",
    flexShrink: 0,
  };

  if (!card) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid #E5E7EB",
        borderRadius: { xs: "12px", lg: "14px" },
        p: { xs: 1.5, md: 2, lg: 2.5, xl: 3 },
        bgcolor: "#ffffff",
        maxWidth: { xs: "100%", xl: "1360px" },
        mx: "auto",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 2.25, lg: 2.5 } }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            p: { xs: 1.5, md: 2, lg: 2.5 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: { xs: 1.5, lg: 3 }, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, lg: 2 } }}>
              <Box
                sx={{
                  width: { xs: 62, lg: 72, xl: 78 },
                  p: { xs: "6px", lg: "7px" },
                  ...scoreRingStyle,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, lg: 18, xl: 19 }, color: "#1F2937" }}>
                    {animatedScore}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: { xs: 9, lg: 10 }, color: "#9CA3AF" }}>/ 100</Typography>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 10, md: 12, lg: 16, xl: 18 }, color: "#111827", lineHeight: 1.2 }}>
                  {card?.title || "Health Score"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: trendColor }}>
                  <TrendIcon sx={{ fontSize: { xs: 16, lg: 18 } }} />
                  <Typography sx={{ fontSize: { xs: 12, lg: 13 }, fontWeight: 600 }}>Trend: {trendText}</Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: 11, lg: 12 }, color: "#6B7280" }}>As of {formatAsOf(card?.as_of)}</Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: { xs: 50, sm: 80, md: 120, lg: 180, xl: 240 },
                height: { xs: 40, md: 54, lg: 64 },
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
                background: "transparent",
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 100 36"
                preserveAspectRatio="none"
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              >
                <defs>
                  <linearGradient id="healthScoreTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(139,92,246,0.22)" />
                    <stop offset="100%" stopColor="rgba(139,92,246,0.03)" />
                  </linearGradient>
                </defs>
                <polygon points="0,10 100,14 100,36 0,36" fill="url(#healthScoreTrendFill)" />
                <polyline
                  points="0,10 20,10.8 40,11.6 60,12.4 80,13.1 100,14"
                  fill="none"
                  stroke="#8B5CF6"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#4B5563" }}>
          Score Trend
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 1.5, lg: 2 } }}>
          {scoreTrendItems.map((item: any, idx: number) => (
            <Paper
              key={`score_trend_${idx}`}
              elevation={0}
              sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", p: { xs: 1.5, lg: 2 } }}
            >
              <Typography sx={{ fontSize: { xs: 10, lg: 11 }, color: "#6B7280", textTransform: "uppercase" }}>{item?.label}</Typography>
              <Typography
                sx={{
                  fontSize: {  xs: 10, md: 12, lg: 16, xl: 18 },
                  fontWeight: 700,
                  color: normalize(item?.label).includes("trend") ? trendColor : "#111827",
                  lineHeight: 1.2,
                }}
              >
                {item?.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#4B5563" }}>
          Key Impact Factors
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 1.5, lg: 2 } }}>
          {keyImpactItems.map((item: any, idx: number) => {
            const parsed = parseFactor(item, idx);
            return (
              <Paper
                key={`key_impact_${idx}`}
                elevation={0}
                sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", p: { xs: 1.5, lg: 2 } }}
              >
                <Typography sx={{ fontSize: { xs: 16, lg: 18 }, fontWeight: 600, color: "#111827", lineHeight: 1.2 }}>
                  {parsed.label}
                </Typography>
                <Typography sx={{ fontSize: { xs: 13, lg: 14 }, color: "#374151" }}>
                  Status: <Box component="span" sx={{ color: "#E53935" }}>{parsed.status || "-"}</Box> | Impact:{" "}
                  <Box component="span" sx={{ color: "#111827", fontWeight: 600 }}>{parsed.impact || "-"}</Box>
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", p: { xs: 1.5, lg: 2 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {factorBars.map((bar, idx) => (
              <Tooltip
                key={`factor_bar_${idx}`}
                arrow
                placement="top"
                title={`${bar.label}: ${bar.barValue}/100${
                  bar.status || bar.impact ? ` (${bar.status ? `Status: ${bar.status}` : ""}${bar.status && bar.impact ? " | " : ""}${bar.impact ? `Impact: ${bar.impact}` : ""})` : ""
                }`}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "88px 1fr 20px", lg: "110px 1fr 28px" },
                    gap: { xs: 1, lg: 1.5 },
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontSize: { xs: 11, lg: 12 }, color: "#4B5563" }}>{bar.label}</Typography>
                  <Box sx={{ height: { xs: 10, lg: 12 }, borderRadius: 999, bgcolor: "#ECECF2", overflow: "hidden" }}>
                    <Box sx={{ width: `${bar.barValue}%`, height: "100%", bgcolor: bar.color, borderRadius: 999 }} />
                  </Box>
                  <Typography sx={{ fontSize: { xs: 10, lg: 11 }, color: "#9CA3AF" }}>{idx === factorBars.length - 1 ? "100" : ""}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Paper>

        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#4B5563" }}>
          Weekly Summary
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 1.5, lg: 2 } }}>
          {weeklySummaryItems.map((item: any, idx: number) => (
            <Paper
              key={`weekly_summary_${idx}`}
              elevation={0}
              sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", p: { xs: 1.5, lg: 2 } }}
            >
              <Typography sx={{ fontSize: { xs: 10, lg: 11 }, color: "#6B7280", textTransform: "uppercase" }}>{item?.label}</Typography>
              <Typography sx={{ fontSize: {  xs: 10, md: 12, lg: 16, xl: 18 }, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {item?.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Typography sx={{ fontSize: { xs: 18, lg: 20 }, fontWeight: 700, color: "#111827" }}>What you can do</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, lg: 1.25 } }}>
          {actionsItems.map((item: any, idx: number) => {
            const Icon = actionIcon(item?.label || "");
            return (
              <Paper
                key={`action_item_${idx}`}
                elevation={0}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  p: { xs: 1.5, lg: 1.75 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, lg: 2 }, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: { xs: 34, lg: 38 },
                      height: { xs: 34, lg: 38 },
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #F1D2D6",
                      color: "#CA4E63",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: 18, lg: 20 } }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, lg: 14 }, color: "#111827" }}>{item?.label}</Typography>
                    <Typography sx={{ fontSize: { xs: 11, lg: 12 }, color: "#6B7280" }}>{item?.value}</Typography>
                  </Box>
                </Box>
                {item?.action && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleItemAction(item.action)}
                    sx={{
                      borderColor: "#E5A2AF",
                      color: "#CA4E63",
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: { xs: 1.5, lg: 2 },
                      py: { xs: 0.5, lg: 0.625 },
                      fontSize: { xs: 12, lg: 13 },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.action?.label || "Select"}
                  </Button>
                )}
              </Paper>
            );
          })}
        </Box>

        {(card?.disclaimers || []).length > 0 && (
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>{card.disclaimers.join(" ")}</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default HealthScoreCard;

